import * as React from 'react';
import { FillParent } from '@components/atoms';

/**
 * Props for the PermissionsWrapper component.
 */
interface PermissionsWrapperProps {
	/**
	 * The user's permissions as an array of strings.
	 */
	userPermissions: string[];
	/**
	 * The required permissions to access the wrapped content.
	 */
	requiredPermissions: string[];
	/**
	 * The user's feature flags as an array of strings.
	 */
	userFeatureFlags?: string[];
	/**
	 * The required feature flags to access the wrapped content.
	 */
	requiredFeatureFlags?: string[];
	/**
	 * The fallback content to display if the user doesn't have the required permissions or feature flags.
	 * @default null
	 */
	fallback?: React.ReactNode;
	/**
	 * The class name to apply to the wrapper.
	 */
	className?: string;
	/**
	 * Whether permissions are still loading.
	 */
	isLoading?: boolean;
}

/**
 * A wrapper component that conditionally renders its children based on the user's permissions and feature flags.
 * @param {PermissionsWrapperProps} props - The props for the PermissionsWrapper component.
 * @returns The wrapped content if the user has the required permissions and feature flags, otherwise the fallback content.
 */
const PermissionsWrapper = ({
	userPermissions,
	requiredPermissions,
	userFeatureFlags = [],
	requiredFeatureFlags = [],
	fallback = null,
	children,
	className,
	isLoading = false,
}: React.PropsWithChildren<PermissionsWrapperProps>) => {
	if (isLoading) {
		return null;
	}

	const hasPermissions = requiredPermissions.every(permission => userPermissions.includes(permission));
	const hasFeatureFlags = requiredFeatureFlags?.every(flag => userFeatureFlags?.includes(flag));

	if (!hasPermissions || !hasFeatureFlags) {
		return fallback ? <FillParent className={className}>{fallback}</FillParent> : null;
	}

	return <FillParent className={className}>{children}</FillParent>;
};

export default PermissionsWrapper;
